/**
 * AudioPlayer — Plan 6 (TTS)
 *
 * Renders native <audio> controls for a base64-encoded WAV blob.
 * - Converts base64 → Blob URL on mount; revokes on unmount (no memory leak)
 * - Playback speed toggle: 1× → 1.5× → 2× → 1×
 * - No external dependencies — uses the browser's built-in audio element
 */

import { useEffect, useRef, useState } from 'react';

interface AudioPlayerProps {
  /** Base64-encoded WAV audio from /api/voice */
  audioBase64: string;
  /** MIME type — always 'audio/wav' from the API */
  mimeType?: string;
}

const SPEEDS = [1, 1.5, 2] as const;
type Speed = typeof SPEEDS[number];

export function AudioPlayer({ audioBase64, mimeType = 'audio/wav' }: AudioPlayerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [speed, setSpeed] = useState<Speed>(1);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Convert base64 → Blob URL
  useEffect(() => {
    if (!audioBase64) return;

    const bytes = Uint8Array.from(atob(audioBase64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);

    // Revoke on cleanup — prevents memory leak
    return () => {
      URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [audioBase64, mimeType]);

  // Apply speed change to the audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, [speed]);

  const cycleSpeed = () => {
    setSpeed(prev => {
      const idx = SPEEDS.indexOf(prev);
      return SPEEDS[(idx + 1) % SPEEDS.length];
    });
  };

  if (!blobUrl) return null;

  return (
    <div className="flex items-center gap-2 mt-3 p-2 bg-[#00d4b8]/5 border border-[#00d4b8]/20 rounded-xl">
      <audio
        ref={audioRef}
        src={blobUrl}
        controls
        className="h-8 flex-1 min-w-0"
        style={{ colorScheme: 'light' }}
      />
      <button
        onClick={cycleSpeed}
        className="shrink-0 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all bg-white text-[#00d4b8] border-[#00d4b8]/40 hover:bg-[#00d4b8] hover:text-white"
        title="Toggle playback speed"
        aria-label={`Playback speed ${speed}×, click to change`}
      >
        {speed}×
      </button>
    </div>
  );
}
